import { useEffect, useState } from 'react';
import {
  Plus, Save, Pencil, Trash2, MapPin, Star, ExternalLink, RefreshCw,
} from 'lucide-react';
import supabase from '../lib/supabase';
import PhotoUploader from '../components/PhotoUploader';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal,
  Field, Input, Checkbox, StatusLine,
} from '../components/ui';

type Venue = {
  id: string;
  club_id: string;
  name: string;
  slug: string | null;
  address: string | null;
  coordinates: { lat: number; lng: number } | null;
  photos: string[];
  is_primary: boolean;
  is_active: boolean;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
};

export default function ClubVenues({ clubId }: { clubId: string }) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [courtCounts, setCourtCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Venue | 'new' | null>(null);
  const [linkingVenueId, setLinkingVenueId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const [vRes, cRes] = await Promise.all([
      supabase.from('club_venues')
        .select('*')
        .eq('club_id', clubId)
        .order('is_primary', { ascending: false })
        .order('name', { ascending: true }),
      supabase.from('courts').select('venue_id').eq('club_id', clubId),
    ]);
    setVenues((vRes.data ?? []) as Venue[]);
    const counts: Record<string, number> = {};
    for (const row of (cRes.data ?? []) as Array<{ venue_id: string }>) {
      counts[row.venue_id] = (counts[row.venue_id] ?? 0) + 1;
    }
    setCourtCounts(counts);
    setLoading(false);
  };

  useEffect(() => { load(); }, [clubId]);

  const setPrimary = async (v: Venue) => {
    if (v.is_primary) return;
    setMsg(null);
    const prior = venues.find((x) => x.is_primary && x.id !== v.id);
    if (prior) {
      const { error } = await supabase.from('club_venues').update({ is_primary: false }).eq('id', prior.id);
      if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    }
    const { error } = await supabase.from('club_venues').update({ is_primary: true }).eq('id', v.id);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setMsg({ kind: 'success', text: `${v.name} is now the primary venue.` });
    load();
  };

  const remove = async (v: Venue) => {
    if (v.is_primary) {
      setMsg({ kind: 'error', text: 'Set another venue as primary before deleting this one.' });
      return;
    }
    if ((courtCounts[v.id] ?? 0) > 0) {
      setMsg({ kind: 'error', text: `Can't delete — ${courtCounts[v.id]} court(s) still assigned. Reassign them first.` });
      return;
    }
    if (!confirm(`Delete venue "${v.name}"? This can't be undone.`)) return;
    const { error } = await supabase.from('club_venues').delete().eq('id', v.id);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setMsg({ kind: 'success', text: 'Venue deleted.' });
    load();
  };

  const openStripeOnboarding = async (v: Venue) => {
    setLinkingVenueId(v.id); setMsg(null);
    const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
      body: { venueId: v.id },
    });
    setLinkingVenueId(null);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
  };

  const refreshStripe = async (v: Venue) => {
    setLinkingVenueId(v.id); setMsg(null);
    const { error } = await supabase.functions.invoke('refresh-stripe-connect', {
      body: { venueId: v.id },
    });
    setLinkingVenueId(null);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setMsg({ kind: 'success', text: 'Stripe status refreshed.' });
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="Club"
        title="Venues"
        subtitle="Physical locations of this club. Courts, hours and closures belong to a venue. If you only have one location, leave the default and all courts live under it."
        actions={<Button icon={Plus} onClick={() => setEditing('new')}>Add venue</Button>}
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <Section className="overflow-hidden p-0">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading venues…</div>
        ) : venues.length === 0 ? (
          <EmptyState
            icon={MapPin}
            title="No venues yet"
            description="Every club needs at least one venue. Add the first one so customers can find it."
            action={<Button icon={Plus} onClick={() => setEditing('new')}>Add a venue</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Venue</th>
                <th className="text-left px-5 py-3 font-semibold">Address</th>
                <th className="text-left px-5 py-3 font-semibold">Courts</th>
                <th className="text-left px-5 py-3 font-semibold">Stripe</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {venues.map((v) => (
                <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{v.name}</span>
                      {v.is_primary && <Badge tone="lime" icon={Star}>Primary</Badge>}
                    </div>
                    {v.slug && <div className="text-xs text-gray-500">/{v.slug}</div>}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{v.address || <span className="text-gray-400">—</span>}</td>
                  <td className="px-5 py-3 text-gray-700">{courtCounts[v.id] ?? 0}</td>
                  <td className="px-5 py-3">
                    {v.stripe_account_id
                      ? v.stripe_onboarding_completed_at
                          ? <Badge tone="green">Connected</Badge>
                          : <Badge tone="amber">KYC pending</Badge>
                      : <Badge tone="gray">Inherits club</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    {v.is_active ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Hidden</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {v.stripe_account_id
                        ? v.stripe_onboarding_completed_at
                            ? <Button size="sm" variant="secondary" icon={RefreshCw}
                                loading={linkingVenueId === v.id}
                                onClick={() => refreshStripe(v)}>Refresh Stripe</Button>
                            : <Button size="sm" icon={ExternalLink}
                                loading={linkingVenueId === v.id}
                                onClick={() => openStripeOnboarding(v)}>Continue KYC</Button>
                        : <Button size="sm" variant="secondary" icon={ExternalLink}
                            loading={linkingVenueId === v.id}
                            onClick={() => openStripeOnboarding(v)}>Onboard Stripe</Button>}
                      {!v.is_primary && (
                        <Button size="sm" variant="secondary" icon={Star} onClick={() => setPrimary(v)}>
                          Make primary
                        </Button>
                      )}
                      <Button size="sm" variant="secondary" icon={Pencil} onClick={() => setEditing(v)}>Edit</Button>
                      {!v.is_primary && (
                        <Button size="sm" variant="danger" icon={Trash2} onClick={() => remove(v)}>Delete</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <VenueForm
          clubId={clubId}
          venue={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function VenueForm({
  clubId, venue, onClose, onSaved,
}: {
  clubId: string;
  venue: Venue | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !venue;
  const [name, setName] = useState(venue?.name ?? '');
  const [slug, setSlug] = useState(venue?.slug ?? '');
  const [address, setAddress] = useState(venue?.address ?? '');
  const [lat, setLat] = useState(venue?.coordinates?.lat?.toString() ?? '');
  const [lng, setLng] = useState(venue?.coordinates?.lng?.toString() ?? '');
  const [isActive, setIsActive] = useState(venue?.is_active ?? true);
  const [photos, setPhotos] = useState<string[]>(venue?.photos ?? []);
  const [venueId, setVenueId] = useState<string | null>(venue?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required.'); return; }
    setBusy(true); setErr(null);
    const parsedLat = lat.trim() ? Number(lat) : null;
    const parsedLng = lng.trim() ? Number(lng) : null;
    if ((parsedLat === null) !== (parsedLng === null)) {
      setBusy(false); setErr('Provide both latitude and longitude, or neither.'); return;
    }
    if (parsedLat !== null && (Number.isNaN(parsedLat) || Number.isNaN(parsedLng))) {
      setBusy(false); setErr('Coordinates must be numbers.'); return;
    }
    const payload = {
      club_id: clubId,
      name: name.trim(),
      slug: slug.trim() || null,
      address: address.trim() || null,
      coordinates: parsedLat !== null ? { lat: parsedLat, lng: parsedLng } : null,
      is_active: isActive,
      photos,
    };
    if (isNew) {
      const { data, error } = await supabase.from('club_venues').insert(payload).select('id').single();
      setBusy(false);
      if (error) { setErr(error.message); return; }
      setVenueId(data.id);
      onSaved();
    } else {
      const { error } = await supabase.from('club_venues').update(payload).eq('id', venue!.id);
      setBusy(false);
      if (error) { setErr(error.message); return; }
      onSaved();
    }
  };

  return (
    <Modal
      title={isNew ? 'Add venue' : `Edit ${venue?.name}`}
      subtitle={isNew
        ? 'A venue is one physical location of this club. Courts get assigned to a venue individually.'
        : undefined}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" hint="e.g. Al Barsha, Jumeirah">
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Slug" hint="Optional URL fragment. Leave blank to auto-generate later.">
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="al-barsha" />
        </Field>
        <Field label="Address">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, area, city" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <Input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="25.1107" />
          </Field>
          <Field label="Longitude">
            <Input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.1830" />
          </Field>
        </div>
        <Checkbox checked={isActive} onChange={setIsActive}
          label="Visible to customers"
          hint="Unchecked = venue stays in admin but won't show up in customer search." />

        <div>
          <div className="text-xs font-medium text-gray-700">Facility photos</div>
          <div className="text-[11px] text-gray-500 mb-2">
            First image is the venue cover on the customer app, and is the fallback hero for any court here that has no photos of its own.
          </div>
          {venueId ? (
            <PhotoUploader
              bucket="club-photos"
              pathPrefix={venueId}
              photos={photos}
              onChange={async (next) => {
                setPhotos(next);
                await supabase.from('club_venues').update({ photos: next }).eq('id', venueId);
              }}
              max={8}
            />
          ) : (
            <div className="text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg p-3">
              Save the venue first, then you'll be able to attach photos.
            </div>
          )}
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            {venueId ? 'Done' : 'Cancel'}
          </Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew && !venueId ? 'Create venue' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
